/**
 * CreatorRedirect - UNIFIED Google OAuth Redirect Handler
 * =======================================================
 * Handles BOTH Partner onboarding AND existing user Gmail upgrades
 * 
 * FLOW DETECTION:
 * - If sessionStorage has 'partner_onboard_data' → New user onboarding → Redirect to /login after success
 * - Else → Existing user upgrade (GoogleRequest flow) → Various redirects based on backend response
 * 
 * EXPECTED URL FORMAT:
 * https://www.useblitz.co/creatorconnect/redirect?code=<auth_code>&state=<request_id>
 *
 * URL PARAMETERS:
 * - code: Authorization code from Google (required)
 * - state: GoogleRequest ID from backend (required for existing users) OR 'partner_onboard' (for new users)
 * - error: OAuth error code (if authorization failed)
 * - error_description: Human-readable error description (if authorization failed)
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, Backdrop, CircularProgress } from "@mui/material";
import AlertDialog from '../Components/AlertDialog';
import useAlertDialog from '../Components/useAlertDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import client from "../API";
import routes from '../Config/routes';
import BlitzHeader from '../Components/BlitzHeader';
import { useCreatorAuth } from '../Hooks/creator-use-auth';
import { toast } from 'sonner';

const CreatorRedirect = () => {
  const navigate = useNavigate();
  const { login } = useCreatorAuth();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const [searchParams] = useSearchParams();

  const redirectStripe = async () => {
    try {
      toast.info('Setting up Stripe', { description: 'Redirecting to Stripe Connect...' });
      const response = await client.creatorConnect.stripeOAuth.initiate('/creatorconnect/dashboard');
      if (response.authorization_url) {
        window.location.href = response.authorization_url;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error) {
      console.error('Stripe OAuth initiation failed:', error);
      toast.error('Failed to connect to Stripe', { description: error.message });
      openDialog("Error", "Failed to start Stripe setup. Please try again from your dashboard.",
        GoToEdit, GoToEdit, 'Ok', null);
    }
  };

  // Debug logging
  console.log('=== GOOGLE OAUTH REDIRECT DEBUG START ===');
  console.log('Full URL:', window.location.href);
  console.log('Code parameter:', searchParams.get('code'));
  console.log('State parameter:', searchParams.get('state'));
  console.log('Error parameter:', searchParams.get('error'));
  
  // ✅ Check for onboarding flow
  const onboardingData = sessionStorage.getItem('partner_onboard_data');
  console.log('Onboarding data present:', !!onboardingData);
  console.log('=== GOOGLE OAUTH REDIRECT DEBUG END ===');

  toast.info('Processing redirect parameters', {
    description: `Search params: ${searchParams.toString()}`
  });

  const GoToApp = (jwt, link, state) => {
    toast.info('Redirecting to app', {
      description: `Target: ${link}`
    });
    if (!link) {
      openDialog('Error', 'The creator of this App has misconfigured settings. Contact them. Error code: error_missing_redirect_url',
        GoToStart, GoToStart, 'Ok', null
      )
      return;
    }
    const queryParams = { jwt };
    if (state) queryParams['state'] = state;
    const queryString = new URLSearchParams(queryParams).toString();
    window.location.href = `${link}?${queryString}`;
  }

  const GoToStart = () => {
    toast.info('Redirecting to start page');
    navigate(`${routes.login}?tab=creator`);
  }

  const GoToEdit = () => {
    navigate(routes.creatorConnectEdit);
  }

  // ✅ Handle Partner onboarding flow (NEW USERS)
  async function HandlePartnerOnboarding(code) {
    console.log('🆕 Processing PARTNER ONBOARDING flow');
    
    try {
      const storedData = sessionStorage.getItem('partner_onboard_data');
      if (!storedData) {
        throw new Error('Session data not found. Please restart the onboarding process.');
      }

      const formData = JSON.parse(storedData);
      console.log('Retrieved form data:', { username: formData.username });

      // Build platform URLs (same logic as PartnerOnboard.js)
      const PLATFORMS = {
        Instagram: { baseUrl: 'https://www.instagram.com/' },
        TikTok: { baseUrl: 'https://www.tiktok.com/@' },
        X: { baseUrl: 'https://www.x.com/' },
        Twitch: { baseUrl: 'https://www.twitch.tv/' },
        Kick: { baseUrl: 'https://www.kick.com/' },
      };

      const platformUrls = {};
      Object.entries(formData.platforms || {}).forEach(([platform, username]) => {
        if (platform === 'YouTube') {
          platformUrls.youtube = `https://www.youtube.com/${username}`;
        } else if (platform === 'Podcast') {
          platformUrls.podcast = username;
        } else if (PLATFORMS[platform]) {
          platformUrls[platform.toLowerCase()] = PLATFORMS[platform].baseUrl + username;
        }
      });

      // Get referrer from stored data
      const referrer = formData.referrer || null;

      // Call onboard_partner endpoint
      const payload = {
        username: formData.username,
        region: formData.region,
        niche: formData.niche,
        phone_number: formData.phone,
        google_code: code,
        supervisor: referrer,
        ...platformUrls
      };

      console.log('Submitting onboarding payload');
      const response = await client.creatorConnect.onboardPartner(payload);

      if (response?.success) {
        // Clear session storage
        sessionStorage.removeItem('partner_onboard_data');

        toast.success('Partner Account Created!', {
          description: 'Your account has been created successfully. Please log in to continue.',
          duration: 5000
        });

        // ✅ CHANGED: Redirect to login page instead of auto-login
        setTimeout(() => {
          navigate(`${routes.login}?tab=creator&signup_success=true${referrer ? `&referrer=${referrer}` : ''}`);
        }, 1500);
      } else {
        throw new Error(response?.error || 'Failed to create account');
      }
    } catch (error) {
      console.error('❌ Onboarding error:', error);
      sessionStorage.removeItem('partner_onboard_data'); // Clear bad data
      
      toast.error('Account Creation Failed', {
        description: error.message || 'Please try again'
      });

      openDialog("Error",
        <>Failed to create Partner account<br />{error.message || error.response?.data?.error || 'Unknown error'}</>,
        GoToStart, GoToStart, 'Ok', null
      );
    }
  }

  // ✅ Handle upgrade flow (EXISTING USERS - GoogleRequest)
  async function HandlePartnerUpgrade(code, state) {
    console.log('⬆️ Processing PARTNER UPGRADE flow (existing user)');

    // Check if Google returned an error
    const oauthError = searchParams.get('error');
    const oauthErrorDescription = searchParams.get('error_description');

    if (oauthError) {
      console.error('OAuth error received from Google:', { error: oauthError, description: oauthErrorDescription });
      toast.error('Google authorization was denied or failed', {
        description: oauthErrorDescription || oauthError
      });
      openDialog("Error",
        `Google OAuth Error: ${oauthErrorDescription || oauthError}. Please try again and make sure to grant the required permissions.`,
        GoToEdit, GoToEdit, 'Ok', null
      );
      return;
    }

    if (code && state) {
      console.log('Both code and state present, sending to backend');
      try {
        const data = await client.creatorConnect.postGoogle({ code, state });
        
        if (data.redirect === 'stripe') {
          login(data.loginData);
          openDialog("Success", "Account created successfully. You will now be redirected to Stripe to set up BlitzPay", 
            redirectStripe, redirectStripe, "Ok", null);
        }
        else if (data.redirect === 'editstripe') {
          openDialog("Success", "Partner Google Account set successfully. You will now be redirected to Stripe to set up BlitzPay", 
            redirectStripe, redirectStripe, "Ok", null);
        }
        else if (data.redirect === 'login') {
          openDialog("Success", data.message || "Account created successfully.", 
            GoToStart, GoToStart, "Ok", null);
        }
        else if (data.redirect === 'edit') {
          toast.success('Gmail Connected Successfully!', {
            description: 'Your Gmail account has been linked. Your AI agent can now manage partnership emails.'
          });
          navigate(routes.creatorConnectEdit + '?upgrade_success=true&tab=partner');
        }
        else if (data.redirect === 'settings') {
          toast.success('Partner Upgrade Successful!', {
            description: data.message || 'You are now a Partner with full AI automation.'
          });
          
          if (data.email_changed && data.new_email) {
            toast.info('Email Updated', {
              description: `Your Gmail forwarding address is now: ${data.new_email}`,
              duration: 5000
            });
          }
          
          navigate(routes.creatorConnectEdit + '?upgrade_success=true&tab=partner');
        }
        else if (data.redirect === 'service') {
          openDialog("Success", "Partner Google Account set successfully. You'll now be redirected to your App",
            () => GoToApp(data.jwt, data.link, data.state), 
            () => GoToApp(data.jwt, data.link, data.state),
            'Ok',
            null);
        }
        else {
          console.warn('Unknown redirect type:', data.redirect);
          toast.success('OAuth Successful', {
            description: data.message || 'Authentication completed'
          });
          navigate(routes.creatorConnectEdit);
        }
      }
      catch (error) {
        console.error('Backend error processing Google code:', error);
        toast.error('Google authorization failed', {
          description: error.response?.data?.error || error.message
        });
        openDialog("Error", 
          <>Error processing your request<br />{error.response?.data?.error || error.message}</>, 
          GoToEdit, GoToEdit, 'Ok', null);
      }
    }
    else {
      console.error('Missing OAuth parameters:', {
        code: code ? 'present' : 'MISSING',
        state: state ? 'present' : 'MISSING',
        fullUrl: window.location.href
      });

      const missingParams = [];
      if (!code) missingParams.push('authorization code');
      if (!state) missingParams.push('state parameter');

      const detailedError = `Missing OAuth parameters: ${missingParams.join(' and ')}`;

      toast.error('OAuth parameters missing', {
        description: detailedError
      });

      openDialog("Error",
        `Unrecoverable error. Your Google authorization was lost. Try again.\n\nTechnical details: ${detailedError}`,
        GoToStart, GoToStart, 'Ok', null
      );
    }
  }

  // ✅ MAIN ROUTER: Detect which flow to use
  async function ProcessOAuthCallback() {
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    // Check for onboarding flow first
    const hasOnboardingData = sessionStorage.getItem('partner_onboard_data');

    if (hasOnboardingData) {
      // NEW USER ONBOARDING FLOW
      console.log('🔀 Detected: ONBOARDING flow (new user signup)');
      await HandlePartnerOnboarding(code);
    } else {
      // EXISTING USER UPGRADE FLOW
      console.log('🔀 Detected: UPGRADE flow (existing user)');
      await HandlePartnerUpgrade(code, state);
    }
  }

  useEffect(() => {
    ProcessOAuthCallback();
  }, [searchParams]);

  return (
    <>
      <BlitzHeader></BlitzHeader>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <AlertDialog alertState={dialogState}></AlertDialog>
      <Box sx={{ p: 2, backgroundColor: '#1a1a1a', color: '#f5f5f5', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom>
          Welcome to Creator Connect
        </Typography>
      </Box>
    </>
  );
};

export default CreatorRedirect;